import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDialerSettings } from '@/contexts/DialerSettingsContext';

/**
 * The « Enregistré » toast of a settings section, on the server's answer
 * (RUI-09), never on a timer.
 *
 * `snapshot` is a serialisation of the settings the section shows. Its first
 * value is hydration, not a user change; every later change is stamped, and
 * only a save issued after that stamp may speak for it (REV-W0 #4: a save
 * sent before the change must not claim it). A failed save offers Réessayer,
 * except a read-only refusal (« ouvrir en tant que », QA-W7-3).
 *
 * Shared by Général and IA (W4): both write caller.dialer_settings through
 * DialerSettingsContext.
 */
export function useSettingsSavedToast(snapshot: string): void {
  const { t } = useTranslation(['settings', 'common']);
  const { settingsSave, retrySettingsSave } = useDialerSettings();
  const initRef = useRef(true);
  const changedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (initRef.current) { initRef.current = false; return; }
    changedAtRef.current = Date.now();
  }, [snapshot]);

  useEffect(() => {
    const changedAt = changedAtRef.current;
    if (!settingsSave || changedAt === null || settingsSave.issuedAt < changedAt) return;
    changedAtRef.current = null;
    if (settingsSave.ok) {
      toast.success(t('settings:toasts.saved'), { duration: 1500 });
    } else if (settingsSave.code === 'IMPERSONATION_READ_ONLY') {
      // QA-W7-3: retrying cannot succeed while viewing as a member.
      toast.error(t('common:errors.IMPERSONATION_READ_ONLY'), { description: settingsSave.code });
    } else {
      toast.error(t('settings:toasts.saveFailed'), {
        action: { label: t('settings:toasts.retry'), onClick: retrySettingsSave },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsSave?.seq]);
}
